import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../App";

const SettingsPage = () => {
  const { user } = useAuth();
  const [offerSettings, setOfferSettings] = useState({
    max_offer_value: 50,
    offer_approval_required_above: 25,
    auto_reject_offers_above: 500,
    offer_alert_emails: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(user?.tenant_id || '');

  const canEditSettings = user?.role === 'super_admin' || user?.role === 'tenant_admin';
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    const fetchTenants = async () => {
      if (isSuperAdmin) {
        try {
          const response = await api.get('/tenants');
          setTenants(response.data);
          if (response.data.length > 0 && !selectedTenantId) {
            setSelectedTenantId(response.data[0].tenant_id);
          }
        } catch (error) {
          console.error('Error fetching tenants:', error);
        }
      }
    };
    fetchTenants();
  }, [isSuperAdmin]);

  useEffect(() => {
    const fetchSettings = async () => {
      const tenantId = isSuperAdmin ? selectedTenantId : user?.tenant_id;
      if (!tenantId) {
        setLoading(false);
        return;
      }
      try {
        const response = await api.get(`/tenants/${tenantId}/offer-settings`);
        setOfferSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
        setOfferSettings({
          max_offer_value: 50,
          offer_approval_required_above: 25,
          auto_reject_offers_above: 500,
          offer_alert_emails: []
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [user?.tenant_id, selectedTenantId, isSuperAdmin]);

  const handleSaveSettings = async () => {
    const tenantId = isSuperAdmin ? selectedTenantId : user?.tenant_id;
    if (!tenantId) return;
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/offer-settings`, offerSettings);
      toast.success('Configurações guardadas com sucesso');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao guardar configurações');
    } finally {
      setSaving(false);
    }
  };

  const addAlertEmail = () => {
    if (newEmail && !offerSettings.offer_alert_emails.includes(newEmail)) {
      setOfferSettings({
        ...offerSettings,
        offer_alert_emails: [...offerSettings.offer_alert_emails, newEmail]
      });
      setNewEmail('');
    }
  };

  const removeAlertEmail = (email) => {
    setOfferSettings({
      ...offerSettings,
      offer_alert_emails: offerSettings.offer_alert_emails.filter(e => e !== email)
    });
  };

  const activeTenantId = isSuperAdmin ? selectedTenantId : user?.tenant_id;

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Configurações</h1>
        <p className="text-muted-foreground">Gerir configurações da organização</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Perfil</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-medium">{user?.name?.charAt(0)}</span>
                )}
              </div>
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <div className="pt-4 border-t border-border">
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Role</dt>
                  <dd className="font-medium">{user?.role}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Tenant</dt>
                  <dd className="font-medium">{user?.tenant_id || 'Não atribuído'}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Conformidade RGPD</h2>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A plataforma está em conformidade com o Regulamento Geral de Proteção de Dados (RGPD).
            </p>
            <div className="space-y-2">
              <button className="w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted transition-colors">
                <p className="font-medium">Exportar Dados</p>
                <p className="text-sm text-muted-foreground">Download dos seus dados pessoais</p>
              </button>
              <button className="w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted transition-colors">
                <p className="font-medium">Gestão de Consentimentos</p>
                <p className="text-sm text-muted-foreground">Gerir permissões de tratamento de dados</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="rounded-xl border bg-card p-6">
          <h2 className="text-lg font-semibold mb-4">Selecionar Organização</h2>
          <p className="text-sm text-muted-foreground mb-4">Como Super Admin, pode visualizar e editar configurações de qualquer organização.</p>
          <select
            value={selectedTenantId}
            onChange={(e) => {
              setSelectedTenantId(e.target.value);
              setLoading(true);
            }}
            className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="tenant-selector"
          >
            <option value="">Selecionar organização...</option>
            {tenants.map((tenant) => (
              <option key={tenant.tenant_id} value={tenant.tenant_id}>
                {tenant.name} ({tenant.tenant_id})
              </option>
            ))}
          </select>
        </div>
      )}

      {activeTenantId && (
        <div className="rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold">Política de Ofertas</h2>
              <p className="text-sm text-muted-foreground">Configurar limites e regras para ofertas recebidas por colaboradores</p>
            </div>
            {canEditSettings && (
              <button
                onClick={handleSaveSettings}
                disabled={saving || loading}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                data-testid="save-offer-settings-btn"
              >
                {saving ? 'A guardar...' : 'Guardar'}
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Valor Máximo Aceitável (€)
                    <span className="text-muted-foreground font-normal ml-1">- Ofertas acima deste valor requerem atenção</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={offerSettings.max_offer_value}
                    onChange={(e) => setOfferSettings({ ...offerSettings, max_offer_value: parseFloat(e.target.value) || 0 })}
                    disabled={!canEditSettings}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    data-testid="max-offer-value-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Aprovação Necessária Acima de (€)
                    <span className="text-muted-foreground font-normal ml-1">- Ofertas abaixo são aprovadas automaticamente</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={offerSettings.offer_approval_required_above}
                    onChange={(e) => setOfferSettings({ ...offerSettings, offer_approval_required_above: parseFloat(e.target.value) || 0 })}
                    disabled={!canEditSettings}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    data-testid="approval-threshold-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Rejeição Automática Acima de (€)
                    <span className="text-muted-foreground font-normal ml-1">- Ofertas acima deste valor são rejeitadas automaticamente</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={offerSettings.auto_reject_offers_above}
                    onChange={(e) => setOfferSettings({ ...offerSettings, auto_reject_offers_above: parseFloat(e.target.value) || 0 })}
                    disabled={!canEditSettings}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    data-testid="auto-reject-threshold-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Emails para Alertas de Ofertas
                  <span className="text-muted-foreground font-normal ml-1">- Recebem notificações quando ofertas excedem limites</span>
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="email@exemplo.pt"
                    disabled={!canEditSettings}
                    className="flex-1 px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                    data-testid="alert-email-input"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAlertEmail())}
                  />
                  <button
                    type="button"
                    onClick={addAlertEmail}
                    disabled={!canEditSettings || !newEmail}
                    className="px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors disabled:opacity-50"
                    data-testid="add-alert-email-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {offerSettings.offer_alert_emails.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">Nenhum email configurado</p>
                  ) : (
                    offerSettings.offer_alert_emails.map((email, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50">
                        <span className="text-sm">{email}</span>
                        {canEditSettings && (
                          <button
                            onClick={() => removeAlertEmail(email)}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500"
                            data-testid={`remove-email-${idx}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-border">
            <h3 className="text-sm font-medium mb-2">Resumo da Política Atual</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Ofertas até <strong className="text-emerald-500">{offerSettings.offer_approval_required_above}€</strong> são aprovadas automaticamente</li>
              <li>• Ofertas entre <strong className="text-amber-500">{offerSettings.offer_approval_required_above}€</strong> e <strong className="text-amber-500">{offerSettings.max_offer_value}€</strong> requerem aprovação</li>
              <li>• Ofertas entre <strong className="text-orange-500">{offerSettings.max_offer_value}€</strong> e <strong className="text-orange-500">{offerSettings.auto_reject_offers_above}€</strong> excedem limite mas podem ser aprovadas</li>
              <li>• Ofertas acima de <strong className="text-red-500">{offerSettings.auto_reject_offers_above}€</strong> são rejeitadas automaticamente</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
